import React, { ChangeEvent, FunctionComponent, useEffect, useState } from 'react'
import { Table, Typography, Col, Row, Input, Button, notification, Space } from 'antd'
import { FilterValue, SorterResult, TablePaginationConfig } from 'antd/lib/table/interface'

import './Users.css'
import { columns } from './columns'
import InviteModal from './InviteModal'

import { DEFAULT_PAGE_SIZE } from '../../../../constants'
import { getUsersList } from '../../../../context/admin/utils'
import { User } from '../../../../context/types'
import { DownloadOutlined } from '@ant-design/icons'
import { useAuthContext } from '../../../../context/auth'

const { Search } = Input
const { Title } = Typography

interface AdminUsersProps {
	state?: number
	forceUpdate?: () => void
}

const Users: FunctionComponent<AdminUsersProps> = (props) => {
	const { state, forceUpdate } = props
	const { auth } = useAuthContext()
	const [loading, setLoading] = useState(false)

	const [filter, setFilter] = useState('')
	const [page, setPage] = useState(1)
	const [column, setColumn] = useState('id')
	const [order, setOrder] = useState<Order>('descend')

	const [total, setTotal] = useState(10)
	const [users, setUsers] = useState<User[]>([])

	const [isInviteModalVisible, setInviteModalVisible] = useState(false)
	const handleSetInviteModalVisible = () => setInviteModalVisible(true)
	const handleSetFilter = (e: ChangeEvent<HTMLInputElement>) => {
		setPage(1)
		setFilter(e.target.value)
	}

	const getUsers = () => {
		setLoading(true)
		getUsersList((page - 1) * DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE, column, order, filter)
			.then((r) => {
				setUsers(r.users.map((v) => ({ ...v, key: v.brand + v.id })))
				setTotal(r.total)
			})
			.catch((err) => {
				notification.error({ message: err.message || 'Server error' })
			})
			.finally(() => {
				setLoading(false)
			})
	}

	useEffect(() => getUsers(), [page, order, column, filter, state])

	const onChange: OnChange = (pagination, _, sorter) => {
		const customSorter = sorter as SorterResult<User>

		setOrder(customSorter.order as Order)
		setColumn(customSorter.columnKey as string)
		setPage(pagination.current)
	}

	const onUpdateStatus = (isUpdated: boolean): void => {
		isUpdated && getUsers()
	}

	return (
		<Space direction="vertical" style={{ width: '100%' }}>
			<Row>
				<Col xs={15} md={10} lg={11}>
					<Title level={3}>Users</Title>
				</Col>
				<Col xs={9} md={4} lg={3}>
					<Button
						type="primary"
						shape="round"
						icon={<DownloadOutlined />}
						size="middle"
						style={{ height: 32, lineHeight: '32px' }}
						href={`${process.env.REACT_APP_API_URL || '/'}api/experts/list/download?token=${auth.token}`}
						target="_blank"
						download
					>
						Experts
					</Button>
				</Col>
				<Col xs={8} md={2} lg={2}>
					<Button onClick={handleSetInviteModalVisible} size="small" style={{ height: 32 }}>
						Invite
					</Button>
				</Col>
				<Col xs={16} md={8} lg={8}>
					<Search
						className="users-search"
						allowClear
						placeholder="Filter"
						value={filter}
						onChange={handleSetFilter}
						onSearch={setFilter}
					/>
				</Col>
			</Row>
			<Table
				columns={columns({ onUpdateStatus })}
				dataSource={users}
				pagination={{ pageSize: DEFAULT_PAGE_SIZE, current: page, total }}
				loading={loading}
				onChange={onChange}
				scroll={{ x: true }}
			/>
			<InviteModal visible={isInviteModalVisible} onChange={setInviteModalVisible} forceUpdate={forceUpdate} />
		</Space>
	)
}

export default Users

type Order = 'ascend' | 'descend' | undefined
type OnChange = (
	pagination: TablePaginationConfig,
	filters: Record<string, FilterValue | null>,
	sorter: SorterResult<any> | SorterResult<any>[]
) => void
